import React from 'react';
import {
  Container,
  Typography,
  Box,
  Breadcrumbs,
  makeStyles,
  createStyles,
  Theme,
} from '@material-ui/core';
import { Link } from 'gatsby-theme-material-ui';

const useStyles = makeStyles(({ spacing }: Theme) =>
  createStyles({
    root: {
      marginTop: spacing(5),
      marginBottom: spacing(5),
    },
    list: {
      listStyle: 'disc',
      marginTop: 'unset',

      paddingLeft: spacing(3),
      '& li': {
        paddingTop: spacing(1),
      },
      '& ul': {
        listStyle: 'circle',
      },
    },
  })
);

export const TermsOfUse = () => {
  const classes = useStyles();
  return (
    <Container maxWidth="md" className={classes.root}>
      <Breadcrumbs aria-label="breadcrumb">
        <Link color="inherit" to="/">
          Home
        </Link>
        <Typography color="textPrimary">Terms of Use</Typography>
      </Breadcrumbs>
      <Box mt={2} />
      <Typography variant="h4" component="h1">
        HabFit Terms of Use
      </Typography>
      <Box mt={3} />
      <Typography>
        The <Link to="./">habfit.io website</Link> (the “<b>Site</b>”) is
        open to both clients and trainers. For the purposes of this Terms of
        use, the Site and any interaction with us on Facebook Messenger,
        WhatsApp, SMS, Slack, Viber, and/or any other online channels shall be
        referred to as “Platform”. The purpose of the Platform is to provide an
        efficient process for connecting clients and trainers via messenger. By
        joining our community, clients will be able to learn about trainers,
        subscribe to remote fitness programs, and gain insight into valuable
        promotions and offerings. Trainers will be able to advertise their
        services, acquire client subscriptions, as well as promote their own
        expertise in the industry. For the purpose of this Terms of Use
        Agreement, both clients and trainers shall be referred to as “<b>you</b>
        ” or “<b>your.</b>”
      </Typography>
      <Box mt={1} />
      <Typography>
        Please understand that, by joining our community and accessing the
        information on the Platform, you are acknowledging that you have read,
        understood, and agree to comply with the terms and conditions stated
        within this Terms of Use Agreement (this &quot;<b>Agreement</b>&quot; ),
        which shall constitute a binding agreement between you, as a user of the
        Platform, and HabFit Technologies Inc. (“<b>HabFit</b>”). This Agreement
        is an electronic contract that sets out the legally binding terms of
        your use and membership of the Platform. If there is any conflict
        between this Agreement and the terms of any other agreement that you
        have entered with HabFit, those terms will supersede the terms of this
        Agreement.
      </Typography>
      <Box mt={3} />
      <Typography id="how-to-use-our-service" variant="h5" component="h2">
        1. How to Use our Service
      </Typography>
      <Box mt={2} />
      <Typography>
        The service is available to both trainers and clients, so that they may
        advertise, connect, and communicate. To enjoy the service, trainers must
        develop a profile and input their locations, availability, and number of
        the available client spots into the HabFit software. The clients may
        then register on the Platform, and begin to search for trainers. Then,
        the client may subscribe to the trainer of his or her choice.
      </Typography>
      <Box mt={3} />
      <Typography id="registering-on-platform" variant="h5" component="h2">
        2. Registering on the Platform
      </Typography>
      <Box mt={2} />
      <Typography>
        Both clients and trainers must register with the Platform, in order to
        enjoy the service that HabFit is offering. To register, clients and
        trainers must provide their full name, state of residence, zip code,
        phone number, date of birth, email address, and create a password.
        Trainers may also upload photos and create bios that provide information
        about their experience and location.
      </Typography>
      <Box mt={1} />
      <Typography>
        You agree that any information that you supply during the registration
        process will be accurate, truthful, and complete. You also agree not to
        (i) select, register, or attempt to register, or use a name of another
        person with the intention of impersonating that person; (ii) use a name
        of anyone else without authorization; or (iii) use any content, names,
        data, or other information that is in violation of the intellectual
        property rights of any person or that HabFit considers to be offensive
        or adverse to the spirit and purpose of the Platform. HabFit reserves
        the right to reject or terminate any registration that, in its judgment,
        it deems offensive or otherwise in violation of this Agreement.
      </Typography>
      <Box mt={1} />
      <Typography>
        You will be responsible for preserving the confidentiality of your
        password and will notify HabFit of any known or suspected unauthorized
        use of your account. Further, you agree that you are responsible for all
        statements made and acts or omissions that occur on your account while
        your password is being used. If you believe someone has used your
        password or account without your authorization, you must notify HabFit
        immediately. HabFit reserves the right to access and disclose any
        information including, without limitation, user names of accounts and
        other information to comply with applicable laws and lawful government
        requests. You must be at least 13 years old to register on the Platform.
      </Typography>
      <Box mt={1} />
      <Typography>
        Please inform HabFit if there is a change in the information you
        provided at the time of your initial registration, including any change
        of address or name, by contacting our representatives at{' '}
        <Link href="mailto:team@habfit.io">team@habfit.io</Link>.
      </Typography>
      <Box mt={3} />
      <Typography id="billing-and-payment" variant="h5" component="h2">
        3. Billing and Payment
      </Typography>
      <Box mt={2} />
      <Typography>
        Billing and Payment The Client shall pay HabFit directly for the
        Services as outlined in the Client’s respective subscription plan. All
        prices and fees displayed on the HabFit Platform are exclusive of
        applicable federal, provincial, state, local, or other governmental
        sales, goods, and services or other taxes, fees, or charges now in force
        or enacted in the future (“Taxes”). Any applicable Taxes are based on
        the rates applicable to the billing address you provide to us and will
        be calculated at the time a transaction is charged to your Account.
        Unless otherwise indicated, all prices, fees, and other charges are in
        Canadian dollars, and all payments shall be in CAD currency.
      </Typography>
      <Box mt={3} />
      <Typography id="fees-refunds-charges" variant="h5" component="h2">
        4. Fees, Refunds, and Charges
      </Typography>
      <Box mt={2} />
      <Typography id="subscription-fees" variant="h6" component="h2">
        Subscription Fees
      </Typography>
      <Box mt={2} />
      <Typography>
        The client shall pay for all subscription fees, and hereby authorizes
        HabFit (and its payment processors) to debit the client’s bank account
        or charge client’s credit card, as stated on client’s channel
        subscription and billing page.{' '}
        <b>
          The client agrees that <i>all</i> subscriptions are recurring and will
          renew automatically.
        </b>{' '}
        The client shall also be solely responsible for paying and remitting to
        the appropriate tax authorities all applicable taxes (withholding tax,
        sales tax, services tax, value-added tax (VAT), goods and services tax
        (GST), etc.) or duties imposed by any government entity or collecting
        agency except those taxes based on HabFit’s net income. In the event
        client fails to satisfy its tax and/or duty obligations herein, client
        shall reimburse HabFit upon demand for any taxes and/or duties paid on
        behalf of client and shall defend, indemnify and hold HabFit harmless
        against any claim and/or liability (including penalties) resulting from
        client’s failure to pay such taxes and/or duties.
      </Typography>
      <Box mt={2} />
      <Typography id="cancellation-policy" variant="h6" component="h2">
        Cancellation Policy
      </Typography>
      <Box mt={2} />
      <Typography>
        The client acknowledges that all subscription fees are charged
        automatically on a recurring basis{' '}
        <b>
          until the client cancels their channel subscription (both
          month-to-month and annual plans)
        </b>
        . The client may cancel their subscription by logging into their{' '}
        <Link href="https://www.paypal.com/signin" target="_blank">
          PayPal Account
        </Link>{' '}
        and cancel their subscription. The client can learn how to{' '}
        <Link
          href="https://www.paypal.com/sm/smarthelp/article/how-do-i-cancel-a-recurring-payment,-subscription,-or-automatic-billing-agreement-i-have-with-a-merchant-faq1067"
          target="_blank"
        >
          cancel their subscription here
        </Link>
        . Or the client can{' '}
        <Link href="mailto:team@habfit.io" target="_blank">
          email support
        </Link>{' '}
        to cancel his subscription . Please keep in mind that{' '}
        <b>
          you are solely responsible for properly canceling your account. You
          must{' '}
          <Link
            href="mailto:team@habfit.io?subject=Subscription%20Cancellation"
            target="_blank"
          >
            email support
          </Link>{' '}
          before or on the payment due date
        </b>
        . However, you can also{' '}
        <Link
          href="mailto:team@habfit.io?subject=Subscription%20Cancellation"
          target="_blank"
        >
          contact support
        </Link>{' '}
        if you are having difficulty or need help.
      </Typography>
      <Box mt={1} />
      <Typography>
        For monthly dues being paid within a Channel, you must contact the
        administrator of the channel to have you removed from the channel,
        cancel your account, or unsubscribe from the channel.
      </Typography>
      <Box mt={2} />
      <Typography id="refunds" variant="h6" component="h2">
        Refunds
      </Typography>
      <Box mt={2} />
      <Typography>
        All HabFit subscriptions include a{' '}
        <b>
          7-day free trial. After 7 days, the chosen Plan Service is billed in
          advance on a monthly or annual basis and is non-refundable; no refunds
          will be issued
        </b>
        . HabFit does not offer prorated refunds for canceled subscription
        plans. There will be no refunds or credits for partial months of
        service, upgrade/downgrade refunds, or refunds for months unused with an
        open account. In order to treat everyone equally, no exceptions will be
        made.
      </Typography>
      <Box mt={1} />
      <Typography>
        <b>
          All subscriptions, both month-to-month and annual plans, are recurring
        </b>{' '}
        and will automatically renew after the end of each paid subscription
        period.
      </Typography>
      <Box mt={1} />
      <Typography>
        <b>If a trainer is unable to continue providing his service</b>, we will
        try to reassign another trainer to you. In case we cannot find a new
        trainer for you, we will cancel the current subscription and refund the
        amount based on the number of days left until the end of the billing
        cycle.
      </Typography>
      <Box mt={3} />
      <Typography id="billing-and-payment" variant="h5" component="h2">
        5. Termination and Suspension
      </Typography>
      <Box mt={2} />
      <Typography>
        Unless otherwise agreed to in writing between you and HabFit, either
        party may terminate these terms of service for any or no cause, at any
        time. You may cancel and delete your Account at any time by either using
        the features on the HabFit Platform to do so or by written or email
        notice to{' '}
        <Link
          href="mailto:team@habfit.io?subject=Cancel%20Account"
          target="_blank"
        >
          team@habfit.io
        </Link>
        . After cancellation, you will no longer have access to your Account,
        your profile, or any other information through the HabFit Platform. The
        provisions of these terms of service which by their intent or meaning
        intended to survive such termination, including without limitation the
        provisions relating to disclaimer of warranties, limitations of
        liability, and indemnification, shall survive any termination of these
        terms of service and any termination of your use of or subscription to
        the HabFir Platform and shall continue to apply indefinitely.
      </Typography>
      <Box mt={3} />
      <Typography
        id="proprietary-rights-to-content"
        variant="h5"
        component="h2"
      >
        6. Proprietary Rights to Content
      </Typography>
      <Box mt={2} />
      <Typography>
        All materials contained on the Platform are protected by copyright law
        except where explicitly noted otherwise. HabFit reserves all rights in
        this content.
      </Typography>
      <Box mt={1} />
      <Typography>
        You acknowledge and agree that content, including but not limited to
        text, software, photographs, video, design, recordings, music, graphics,
        or other material contained on the Platform (the “<b>Content</b>”) is
        protected by copyrights, trademarks, service marks, patents, or other
        proprietary rights and applicable law. You agree not to copy, reproduce,
        distribute, or create derivative works from the Content or otherwise
        use, transmit, rebroadcast, publish, or distribute the Content in any
        form other than as expressly authorized by this Agreement without
        HabFit’ prior written consent. You understand and agree that you are
        permitted to make one copy of the Content for personal use only,
        provided you:
      </Typography>
      <Box mt={1} />
      <ul className={classes.list}>
        <li>
          <Typography>
            Keep all copyright and other proprietary notices on each copy you
            make
          </Typography>
        </li>
        <li>
          <Typography>
            Use the material in a manner consistent with this Agreement
          </Typography>
        </li>
        <ul className={classes.list}>
          <li>
            <Typography>
              Understand that we are neither transferring ownership of the
              materials directly or by implication, nor granting any license or
              right to the trademarks, tradenames, or copyrights of any party
            </Typography>
          </li>
        </ul>
      </ul>
      <Box mt={1} />
      <Typography>
        Using any of our materials for a commercial purpose without our express
        written consent-violates our copyrights and other proprietary rights.
      </Typography>
      <Box mt={1} />
      <Typography>
        You may not use any data mining, robots, scraping, or similar data
        gathering and extraction tools on the Content, frame or scrape data from
        any portion of the Platform or Content, or reproduce, reprint, copy,
        store, publicly display, broadcast, transmit, modify, translate,
        publish, sublicense, assign, transfer, sell, loan, or otherwise
        distribute the Content without our prior written consent. You may not
        circumvent any mechanisms included in the Content for preventing the
        unauthorized reproduction or distribution of the Content. You also are
        prohibited from taking any action that imposes or may impose (in our
        sole discretion) an unreasonable or disproportionately large load on our
        infrastructure. Finally, you may not utilize any device, software, or
        routine that will interfere or attempt to interfere with the
        functionality of the Platform.
      </Typography>
      <Box mt={3} />
      <Typography id="user-conduct" variant="h5" component="h2">
        7. User Conduct
      </Typography>
      <Box mt={2} />
      <Typography>
        You agree that all the information that you access on the Platform will
        be used only for your own personal purposes. You will not make any other
        unauthorized use of the Platform or any interactive features available
        on the Platform or otherwise act in a manner that is disruptive to the
        purpose and intent of the Platform. You may not engage in any conduct or
        action that is prohibited by any applicable law.
      </Typography>
      <Box mt={3} />
      <Typography id="privacy" variant="h5" component="h2">
        8. Privacy
      </Typography>
      <Box mt={2} />
      <Typography>
        HabFit collects information about the users of the Site. The collection
        of this information is governed by our Privacy Policy, which may be
        accessed at{' '}
        <Link to="./privacy-policy">habfit.io/privacy-policy</Link>.
      </Typography>
      <Box mt={3} />
      <Typography id="waiver" variant="h5" component="h2">
        9. Waiver
      </Typography>
      <Box mt={2} />
      <Typography>
        You acknowledge that training can be dangerous. The facilities at which
        a session takes place may contain hazards that could result in your
        injury or death or damage to your property. The equipment used at a
        session could cause your injury or death or damage to your property.
        Your client or trainer could cause your injury or death or damage to
        your property. You assume all risk of injury, death, and property damage
        related to all sessions scheduled through the Platform. HabFit does not
        inspect or maintain any facilities at which any lesson takes place, does
        not provide any equipment used at any session, and is not responsible
        for the behavior of any client or trainer. By scheduling a session or
        subscribing to the trainer service, you are agreeing that HabFit has no
        liability whatsoever for any injury, death, or property damage suffered
        by you in connection with such session.
      </Typography>
      <Box mt={3} />
      <Typography
        id="corporate-identification-and-trademarks"
        variant="h5"
        component="h2"
      >
        10. Corporate Identification and Trademarks
      </Typography>
      <Box mt={2} />
      <Typography>
        All registered and unregistered trademarks and service marks
        (collectively, “<b>Marks</b>”) used or referred to on the Platform are
        the property of HabFit or its affiliate, unless otherwise noted. You may
        not use, copy, reproduce, republish, upload, post, transmit, distribute,
        or modify these Marks in any way without HabFit’s prior written
        permission. The use of Habfit’ Marks on any other website or platform,
        without authorization, is prohibited.
      </Typography>
      <Box mt={3} />
      <Typography id="public-areas-of-the-site" variant="h5" component="h2">
        11. Public Areas of the Site; IP Warranty and Grant
      </Typography>
      <Box mt={2} />
      <Typography>
        Certain areas of the Platform may allow both trainers and clients to
        post their own Content (as defined in Paragraph 5), which can be
        accessed and viewed by others, including the public in general. For
        example, the trainers may upload photos and biographical information
        that is created by the trainer for the purpose of publication on the
        Platform. Please know that you may only post such Content to public
        areas on the Platform that you created or that you have permission to
        post. You may not post any Content that violates these Terms of Use.
      </Typography>
      <Box mt={1} />
      <Typography>
        We do not claim ownership in any Content (as defined above) that you may
        post. However, by submitting such Content to public areas of the Site,
        you hereby warrant that you own the appropriate rights in the Content,
        so as not to violate any third party’s intellectual property or
        proprietary rights. Further, you grant to HabFit a worldwide,
        royalty-free, perpetual, irrevocable, non-exclusive right and license to
        use, reproduce, modify, adapt, publish, translate, create derivative
        works from, distribute, perform, and display such Content, including any
        message or any e-mail sent by you to us or posted on the Platform (in
        whole or in part), and/or to incorporate it in other works in any form,
        media, or technology now known or later developed without the need to
        attributed authorship.
      </Typography>
      <Box mt={1} />
      <Typography>
        To be clear, you understand and agree that you may not upload, post, or
        otherwise make available on the Platform any Content protected by
        copyright, trademark, or other proprietary rights without the express
        permission of the owner of the copyright, trademark, or other
        proprietary rights. You are responsible for determining that such
        material is not protected by copyright, trademark, or other proprietary
        rights. You shall be solely liable for any damages resulting from any
        infringement of copyright, trademark, or other proprietary rights, or
        any other harm resulting from any uploading, posting, or submission.
      </Typography>
      <Box mt={3} />
      <Typography id="prohibited-conduct" variant="h5" component="h2">
        12. Prohibited Conduct
      </Typography>
      <Box mt={2} />
      <Typography>
        By using the Platform you agree not to submit, post, or transmit any
        content or otherwise to engage in any conduct, that violates any of the
        following rules:
      </Typography>
      <Box mt={1} />
      <ul className={classes.list}>
        <li>
          <Typography>
            You may not attempt to harm, disrupt, or otherwise engage in
            activity that impairs, the Platform.
          </Typography>
        </li>
        <li>
          <Typography>
            You may not post any content on the Platform that (a) violates or
            encourages others to violate any applicable law or regulation, or
            which would give rise to civil liability; (b) is fraudulent, false,
            misleading, or deceptive; (c) is defamatory, pornographic, obscene,
            vulgar, or offensive; (d) promotes bigotry, racism, hatred,
            harassment, or harm against any individual or group; or (e) is
            abusive or threatening.
          </Typography>
        </li>
        <li>
          <Typography>
            You may not attempt to interfere with any other person’s use of the
            Platform or the services offered on the Platform.
          </Typography>
        </li>
        <li>
          <Typography>
            You may not misrepresent your identity or impersonate any person.
          </Typography>
        </li>
        <li>
          <Typography>
            You may not access, descramble, deactivate, remove, impair bypass,
            or circumvent any technological measure that we, our partners, or a
            third party have implemented to protect the Platform.
          </Typography>
        </li>
        <li>
          <Typography>
            You may not attempt to gain access to any account, computers or
            networks related to or used in connection with the Platform, without
            authorization, or otherwise tamper with any aspect of the Platform,
            or use any robot, spider, data mining tool, web tool, engine,
            software, device, or mechanism to access data on the Platform,
            unless we provided you with such tool and authorized you to use it
            for the specific purpose(s) in which you have used it
          </Typography>
        </li>
        <li>
          <Typography>
            You may not attempt to obtain any data through any means from the
            Platform, except if we intend to provide or make it available to
            you.
          </Typography>
        </li>
        <li>
          <Typography>
            You may not use the Platform to participate in pyramid schemes or
            chain letters.
          </Typography>
        </li>
        <li>
          <Typography>
            You may not use the Platform to send, either directly or indirectly,
            any unsolicited bulk email or communications or unsolicited
            commercial email or communications.
          </Typography>
        </li>
        <li>
          <Typography>
            You may not use the Platform to post, display, send or otherwise
            make available or use, any material protected by intellectual
            property laws unless you own or control all necessary rights to such
            material or have received all necessary authorization.
          </Typography>
        </li>
        <li>
          <Typography>
            You may not use the Platform to send or otherwise make available any
            material that contains viruses, Trojan horses, worms, corrupted
            files, or any other similar software that may damage the operation
            of any computer or property.
          </Typography>
        </li>
        <li>
          <Typography>
            You may not use the Platform to download any material sent by
            another user of the Platform that you know, or reasonably should
            know, cannot be legally distributed in such a manner.
          </Typography>
        </li>
        <li>
          <Typography>
            You may not use the Platform in a manner that violates this
            Agreement, or any code of conduct or other guidelines which may be
            applicable to the Platform.
          </Typography>
        </li>
        <li>
          <Typography>
            You may not use the Platform to harvest or otherwise collect
            information about others, including without limitation email
            addresses.
          </Typography>
        </li>
        <li>
          <Typography>
            You may not attempt to modify, translate, adapt, edit, copy,
            decompile, disassemble, or reverse engineer any software used or
            provided by us in connection with the Platform.
          </Typography>
        </li>
        <li>
          <Typography>
            You may not use the Platform in a manner that results in excessive
            bandwidth usage, as determined solely by us.
          </Typography>
        </li>
      </ul>
      <Box mt={1} />
      <Typography>
        We have the right to make all judgments concerning any of the above
        prohibitions in our sole, exclusive, and complete discretion. We reserve
        the right, also in our sole discretion, to determine whether and what
        action to take in response to any violation or potential violation of
        this Agreement, and any action or inaction in a particular instance
        shall not dictate or limit our response to a future complaint or
        situation.
      </Typography>
      <Box mt={3} />
      <Typography id="link-to-third-party-sites" variant="h5" component="h2">
        13. Links to Third Party Sites
      </Typography>
      <Box mt={2} />
      <Typography>
        At times, the Platform may contain links to third-party websites, which
        are not under the control of HabFit. HabFit makes no representations
        whatsoever about any other website to which you may have access through
        the Site. When you access another website, you do so at your own risk
        and acknowledge that FitnessTrainer is not responsible or liable for any
        content, advertising, services, products, or other materials available
        from such third-party websites. You also agree that HabFit shall not be
        liable for any loss or damage of any sort incurred as the result of
        using any third party’s website. Mention of third-party companies and
        websites on the Platform is for informational purposes only and does not
        constitute an endorsement or recommendation.
      </Typography>
      <Box mt={3} />
      <Typography id="email-policy" variant="h5" component="h2">
        14. Email Policy
      </Typography>
      <Box mt={2} />
      <Typography>
        You may receive periodic emails from HabFit. If you would rather not
        receive an email from HabFit, please send an email to{' '}
        <Link href="mailto:team@habfit.io?subject=Email%Policy" target="_blank">
          team@habfit.io
        </Link>{' '}
        and you will be unsubscribed from receiving further mailings. You
        acknowledge and agree, however, that you will still receive subscription
        and schedule confirmation emails, session and subscription change
        confirmation emails, subscription and session cancellation confirmation
        emails, and other emails relating to any sessions and subscription that
        are booked through the Platform, even if you have opted not to receive
        periodic email from HabFit.
      </Typography>
      <Box mt={3} />
      <Typography id="limitations-liability" variant="h5" component="h2">
        15. Limitation of Liability
      </Typography>
      <Box mt={2} />
      <Typography>
        IN NO EVENT SHALL HABFIT BE LIABLE FOR ANY INJURY, LOSS, CLAIM, OR
        DAMAGES (INCLUDING INDIRECT, SPECIAL, EXEMPLARY, PUNITIVE, INCIDENTAL,
        OR CONSEQUENTIAL DAMAGES) OF ANY KIND, WHETHER BASED IN CONTRACT, TORT,
        OR OTHERWISE, WHICH ARISES OUT OF OR IS ANY WAY CONNECTED WITH (I) ANY
        USE OF THE SERVICES, THE SITE, OR ITS CONTENT, (II) ANY FAILURE OR DELAY
        (INCLUDING, BUT NOT LIMITED TO, THE USE OR INABILITY TO USE ANY
        COMPONENT OF THE SERVICES OR THE PLATFORM FOR SCHEDULING PURPOSES), OR
        (III) THE PERFORMANCE OR NON-PERFORMANCE OF ANY TRAINER OR CLIENT IN
        CONNECTION WITH THE SERVICES, EVEN IF HABFIT HAS BEEN ADVISED OF THE
        POSSIBILITY OF SUCH DAMAGES.
      </Typography>
      <Box mt={3} />
      <Typography id="disclaimer-of-warranties" variant="h5" component="h2">
        16. Disclaimer of Warranties
      </Typography>
      <Box mt={2} />
      <Typography>
        HabFit makes no warranty that the Platform will be uninterrupted,
        timely, secure, or error-free. HabFit makes no warranty as to the
        accuracy of the information on the Platform.
      </Typography>
      <Box mt={1} />
      <Typography>
        UNLESS EXPRESSLY STATED OTHERWISE, HABFIT PROVIDES THE CONTENT ON THE
        SITE “AS IS” AND WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR
        IMPLIED, TO THE FULLEST EXTENT ALLOWABLE BY APPLICABLE LAW, INCLUDING
        THE IMPLIED WARRANTIES OF MERCHANTABILITY, NONINFRINGEMENT OF
        INTELLECTUAL PROPERTY, AND FITNESS FOR A PARTICULAR PURPOSE.
      </Typography>
      <Box mt={3} />
      <Typography id="indemnification" variant="h5" component="h2">
        17. Indemnification
      </Typography>
      <Box mt={2} />
      <Typography>
        You agree to indemnify, defend, and hold harmless HabFit, its officers,
        directors, employees, agents, affiliates, distributors, and licensees
        from and against any judgment, losses, deficiencies, damages,
        liabilities, costs, and expenses (including reasonable attorneys’ fees
        and expenses) incurred in connection with or arising from any claim,
        demand, suit, action, or proceeding arising out of any breach of this
        Agreement by you or in connection with your use of the Platform or any
        service (or product) related thereto.
      </Typography>
      <Box mt={3} />
      <Typography id="indemnification" variant="h5" component="h2">
        18. Right to Modify or Change
      </Typography>
      <Box mt={2} />
      <Typography>
        HabFit reserves the right to modify this Agreement periodically at its
        sole discretion. When it makes any modification to this Agreement, it
        will update the date referenced at the end of this page. Your continued
        use of the Platform constitutes acceptance of the terms and conditions
        stated at the time of use.
      </Typography>
      <Box mt={3} />
      <Typography id="electronic-signature" variant="h5" component="h2">
        19. Electronic Signature
      </Typography>
      <Box mt={2} />
      <Typography>
        The Terms of Use Agreement is an electronic contract that governs your
        use of and access to the Platform and Services. Upon agreeing to the
        Terms of Use when enrolling for the Instructor Application and Customer
        Checkout, you acknowledge that you understand and agree to be legally
        bound by the Terms of Use Agreement and the terms, conditions, and
        notices contained or referenced herein. The click on “I agree” (or
        similar button or checkbox) creates an electronic signature that has the
        same legal force and effect as a handwritten signature.
      </Typography>
      <Box mt={3} />
      <Typography id="electronic-signature" variant="h5" component="h2">
        20. Waiver/Severability
      </Typography>
      <Box mt={2} />
      <Typography>
        The waiver by either party of a breach or right under this Agreement
        will not constitute a waiver of any subsequent breach or right. If any
        provision of this Agreement is found to be invalid or unenforceable by a
        court of competent jurisdiction, such provision shall be severed from
        the remainder of this Agreement, which will otherwise remain in full
        force and effect.
      </Typography>
      <Box mt={3} />
      <Typography variant="subtitle1">Last updated March 30, 2021.</Typography>
    </Container>
  );
};
